import { Injectable } from "@angular/core";
import { ApiResult, ApiService } from "./api.service";
import { map, Observable, retry } from "rxjs";
import {
    EbayMarketplace,
    EbayMarketplaceCollection,
    EbayMarketplaceOffer,
    EbayMarketplaceResetTraductionResult, EbayMarketplaceTraductionResult,
    EbayPhoto,
    EbayPhotoCollection,
    EbayPhotoFilters,
    EbayProduct,
    EbayProductAnalysis,
    EbayProductChartAnalysis,
    EbayProductCollection,
    EbayProductFilters,
    EbayStock,
    EbayStockCollection, EbayStockDebug,
    EbayStockFilters, EbayStockPriceAnalysisCollection
} from "../interfaces/ebay";

@Injectable({
  providedIn: 'root'
})
export class EbayService extends ApiService {
  /**
   * Ebay APIs
   */

  public postEbayStockPublish(id: string | number): Observable<EbayProduct> {
    return this.http.post<EbayProduct>(`${this.apiUrl}/ebay/publier/${id}`, {})
  }

  public getEbayAnalyseMarche(id: string | number): Observable<EbayProductAnalysis> {
    return this.http.get<EbayProductAnalysis>(`${this.apiUrl}/ebay/analyses/${id}/latest`)
  }

  public getEbayAnalyseMarcheChart(id: string | number): Observable<EbayProductChartAnalysis[]> {
    return this.http.get<EbayProductChartAnalysis[]>(`${this.apiUrl}/ebay/analyses/${id}`)
  }

  public getEbayAnalyseStocksPrice(filters: EbayStockFilters = {}): Observable<EbayStockPriceAnalysisCollection> {
      const queryString = this.convertObjectToQueryString(filters)
      return this.http.get<EbayStockPriceAnalysisCollection>(`${this.apiUrl}/ebay/listing/analyses/opportunites-prix${queryString}`)
  }

  /**
   * Ebay Product APIs
   */

  public getEbayProduct(id: string | number, filters: EbayProductFilters = {}): Observable<EbayProduct> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<ApiResult<EbayProduct>>(`${this.apiUrl}/produits-ebay/${id}${queryString}`)
      .pipe(map((ar: ApiResult<EbayProduct>) => ar.result))
  }

  public getEbayProductCollection(filters: EbayProductFilters = {}): Observable<EbayProductCollection> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<EbayProductCollection>(`${this.apiUrl}/produits-ebay/${queryString}`)
  }

  public postEbayProduct(data: Partial<EbayProduct>, filters: EbayProductFilters = {}): Observable<EbayProduct> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.post<EbayProduct>(`${this.apiUrl}/produits-ebay/${queryString}`, data)
  }

  public patchEbayProduct(id: string | number, data: Partial<EbayProduct>, filters: EbayProductFilters = {}): Observable<EbayProduct> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.patch<EbayProduct>(`${this.apiUrl}/produits-ebay/${id}${queryString}`, data)
  }

  public deleteEbayProduct(id: string | number): Observable<string> {
    return this.http.delete<string>(`${this.apiUrl}/produits-ebay/${id}`)
  }

  /**
   * Ebay Stock APIs
   */

  public getEbayStock(id: string | number, filters: EbayStockFilters = {}): Observable<EbayStock> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<ApiResult<EbayStock>>(`${this.apiUrl}/stocks-ebay/${id}${queryString}`)
      .pipe(map((ar: ApiResult<EbayStock>) => ar.result))
  }

  public getEbayStockCollection(filters: EbayStockFilters = {}): Observable<EbayStockCollection> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<EbayStockCollection>(`${this.apiUrl}/stocks-ebay/${queryString}`)
  }

  public postEbayStock(data: Partial<EbayStock>, filters: EbayStockFilters = {}): Observable<EbayStock> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.post<EbayStock>(`${this.apiUrl}/stocks-ebay/${queryString}`, data)
  }

  public postRemoveBackgroundEbayStock(id: string | number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/stocks-ebay/${id}/suppression-arriere-plan`, {})
      .pipe(retry(0));
  }

  public postReInitEbayStockPhotos(id: string | number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/stocks-ebay/${id}/re-initialiser-photos`, {})
  }

  public postDebugEbayStock(id: string | number): Observable<EbayStockDebug> {
    return this.http.post<any>(`${this.apiUrl}/stocks-ebay/${id}/debug-sku`, {})
  }

  public patchEbayStock(id: string | number, data: Partial<EbayStock>, filters: EbayStockFilters = {}): Observable<EbayStock> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.patch<EbayStock>(`${this.apiUrl}/stocks-ebay/${id}${queryString}`, data)
  }

  public deleteEbayStock(id: string | number): Observable<string> {
    return this.http.delete<string>(`${this.apiUrl}/stocks-ebay/${id}`)
  }

  /**
   * Ebay Photo APIs
   */

  public getEbayPhoto(id: string | number, filters: EbayPhotoFilters = {}): Observable<EbayPhoto> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<ApiResult<EbayPhoto>>(`${this.apiUrl}/photos-ebay/${id}${queryString}`)
      .pipe(map((ar: ApiResult<EbayPhoto>) => ar.result))
  }

  public getEbayPhotoCollection(filters: EbayPhotoFilters = {}): Observable<EbayPhotoCollection> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<EbayPhotoCollection>(`${this.apiUrl}/photos-ebay/${queryString}`)
  }

  public postEbayPhoto(data: Partial<EbayPhoto>, filters: EbayPhotoFilters = {}): Observable<EbayPhoto> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.post<EbayPhoto>(`${this.apiUrl}/photos-ebay/${queryString}`, data)
  }

  public postEbayPhotoOrder(data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/photos-ebay/reorganise_order`, data)
  }

  public patchEbayPhoto(id: string | number, data: Partial<EbayPhoto>, filters: EbayPhotoFilters = {}): Observable<EbayPhoto> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.patch<EbayPhoto>(`${this.apiUrl}/photos-ebay/${id}${queryString}`, data)
  }

  public deleteEbayPhoto(id: string | number): Observable<string> {
    return this.http.delete<string>(`${this.apiUrl}/photos-ebay/${id}`)
  }

  /**
   * Ebay Shops APIs
   */

  public getEbayMarketplace(filters: any = {}): Observable<EbayMarketplaceCollection> {
    const queryString = this.convertObjectToQueryString(filters)
    return this.http.get<EbayMarketplaceCollection>(`${this.apiUrl}/marketplaces-ebay/${queryString}`)
  }

  public getEbayMarketplaceOffers(id: string | number, filters: any = {}): Observable<any> {
      const queryString = this.convertObjectToQueryString(filters)
      return this.http.get<any>(`${this.apiUrl}/marketplaces-ebay/offres/${id}${queryString}`)
  }

  public patchEbayMarketplace(id: string | number, data: Partial<EbayMarketplace>): Observable<EbayMarketplace> {
    return this.http.patch<EbayMarketplace>(`${this.apiUrl}/marketplaces-ebay/${id}`, data)
  }

  public patchEbayMarketplaceTraduction(id_stock: string | number, id_marketplace: string | number, data: any = {}): Observable<EbayMarketplaceTraductionResult> {
      return this.http.patch<EbayMarketplaceTraductionResult>(`${this.apiUrl}/marketplaces-ebay/offres/${id_stock}/${id_marketplace}`, data)
  }

  public postEbayMarketplaceSync(id: string | number, data: any = {}): Observable<any> {
    const queryString = this.convertObjectToQueryString(data)
    return this.http.post<any>(`${this.apiUrl}/marketplaces-ebay/synchroniser/${id}${queryString}`, {})
  }

  public postEbayMarketPlaceExclude(id: string | number, data: any = {}): Observable<any> {
    const queryString = this.convertObjectToQueryString(data)
    return this.http.post<any>(`${this.apiUrl}/marketplaces-ebay/retirer/${id}${queryString}`, {})
  }

  public postEbayMarketPlaceInclude(id: string | number, data: any = {}): Observable<any> {
    const queryString = this.convertObjectToQueryString(data)
    return this.http.post<any>(`${this.apiUrl}/marketplaces-ebay/reactiver/${id}${queryString}`, {})
  }

  public postEbayMarketplaceResetTraduction(id: string | number, data: any = {}): Observable<EbayMarketplaceResetTraductionResult> {
    const queryString = this.convertObjectToQueryString(data)
    return this.http.post<EbayMarketplaceResetTraductionResult>(`${this.apiUrl}/marketplaces-ebay/traduire/${id}${queryString}`, {})
  }
}
